import * as React from 'react';
import { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  CanAccessPartnerData,
  CanAccessUserData,
  Viewer,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import { IconButton } from '../../components/Button/Button';
import { FaUnlink } from 'react-icons/fa';
import { ContentSection } from '../../components/Layout/ContentSection';

interface OfflineCardInfo {
  userId: string;
  signupCode: string;
  offlineCardLinkedByUserUserName: string;
  offlineCardLinkedByUserUserId: string;
  offlineCardLinkedAt: string;
}

interface OfflineCardTabPageProps {
  vytalStoreGuid: string;
  title: string;
}

interface OfflineCardTabPageState {
  storeId: string;
  isLoading: boolean;
  offlineCards: OfflineCardInfo[];
  selection: OfflineCardInfo[];
  loading: boolean;
}

export class OfflineCardTab extends Component<
  OfflineCardTabPageProps,
  OfflineCardTabPageState
> {
  static contextType = ViewerContext;
  constructor(props: OfflineCardTabPageProps) {
    super(props);

    this.state = {
      storeId: this.props.vytalStoreGuid,
      isLoading: false,
      offlineCards: [],
      selection: [],
      loading: false,
    };
  }

  componentDidMount() {
    this.updateData();
  }

  componentWillUpdate(
    newProps: OfflineCardTabPageProps,
    newState: OfflineCardTabPageState,
  ) {
    if (newProps.vytalStoreGuid != this.props.vytalStoreGuid) {
      this.setState({ storeId: newProps.vytalStoreGuid }, this.updateData);
    }
  }

  updateData() {
    this.setState({ isLoading: true });
    Communicator.Get('/Merchant/GetLinkedOfflineCards', {
      storeId: this.state.storeId,
    })
      .then((response) => response.json())
      .then((offlineCards: OfflineCardInfo[]) => {
        if (offlineCards) {
          this.setState({ offlineCards: offlineCards });
        }
        this.setState({ isLoading: false });
      });
  }

  renderUserCell(ownerId: string | null, ownerName: string | null) {
    //only link if owner id is not null and viewer is admin
    const href =
      ownerId !== null && CanAccessUserData(this.context as Viewer)
        ? { href: `/users/${ownerId}` }
        : {};
    return (
      <a style={{ wordWrap: 'break-word' }} {...href}>
        {ownerName ?? i18n.t('Common:unknown')}
      </a>
    );
  }

  unlinkOfflineCards() {
    this.setState({ loading: true });
    Communicator.PostData(
      '/api/3/OfflineCard/UnlinkOfflineCardsFromStore',
      this.state.selection.map((o) => o.userId),
    ).then(() => this.updateData());
  }

  render() {
    return (
      <ContentSection
        title={this.props.title}
        collapsable={false}
        key="content"
      >
        <h2 style={{ textAlign: 'center' }}>{i18n.t('OfflineCards:title')}</h2>
        {CanAccessPartnerData(this.context as Viewer) && (
          <IconButton
            label={i18n.t('OfflineCards:unlink', {
              count: this.state.selection.length,
            })}
            icon={<FaUnlink />}
            style={{ float: 'right' }}
            onClick={() => this.unlinkOfflineCards()}
            disabled={this.state.isLoading || this.state.selection.length === 0}
            className={'mt-3 mb-3'}
          />
        )}
        <DataTable
          selection={this.state.selection}
          selectionMode="multiple"
          onSelectionChange={(e) => this.setState({ selection: e.value })}
          value={this.state.offlineCards}
          dataKey="userId"
          lazy
          loading={this.state.isLoading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column headerStyle={{ width: '50px' }} selectionMode="multiple" />
          <Column
            headerStyle={{ width: '200px' }}
            body={(o: OfflineCardInfo) =>
              this.renderUserCell(o.userId, o.signupCode)
            }
            header={i18n.t('OfflineCards:signupCodes')}
          />
          {CanAccessPartnerData(this.context as Viewer) && (
            <Column
              headerStyle={{ width: '200px' }}
              body={(o: OfflineCardInfo) =>
                this.renderUserCell(
                  o.offlineCardLinkedByUserUserId,
                  o.offlineCardLinkedByUserUserName,
                )
              }
              header={i18n.t('OfflineCards:linkedBy')}
            />
          )}
          {CanAccessPartnerData(this.context as Viewer) && (
            <Column
              headerStyle={{ width: '200px' }}
              body={(o: OfflineCardInfo) =>
                o.offlineCardLinkedAt
                  ? new Date(o.offlineCardLinkedAt).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(),
                    )
                  : ''
              }
              header={i18n.t('OfflineCards:linkedAt')}
            />
          )}
        </DataTable>
      </ContentSection>
    );
  }
}
